import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Loader2,
  Building2,
  Palette,
  Plus,
  Shield,
  Undo2,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

import { cn } from "@/lib/utils/cn";
import { toast } from "@/lib/toast";
import { usePermissions } from "@/lib/hooks/use-permissions";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import {
  useOrganizationsCustomerApplyLogoEdit,
  useOrganizationsCustomerDeleteLogo,
  useOrganizationsCustomerFindOneQuery,
  useOrganizationsCustomerPreviewBackgroundRemoval,
  useOrganizationsCustomerRestoreOriginalLogo,
  useOrganizationsCustomerUpdate,
  useOrganizationsCustomerUploadLogo,
} from "@/lib/api/requests/organizations.requests";
import {
  OrganizationCustomerDetailsResponseDto,
} from "@/lib/api/interfaces/organizations.interface";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { Card, CardContent, CardHeader } from "@/ui-components/ui/card";
import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { ManagedImageBusyAction, ManagedImageField, persistManagedImageDraft } from "@/util-components/media";

const brandingSchema = z.object({
  name: z.string().min(1, "Vereinsname ist erforderlich."),
  primaryColor: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6})$/, "Bitte eine HEX-Farbe wie #0055FF angeben."),
  secondaryColor: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6})$/, "Bitte eine HEX-Farbe wie #F4F7FB angeben."),
  newLogo: z.instanceof(File).optional(),
});

type BrandingFormValues = z.infer<typeof brandingSchema>;

const getDefaultValues = (organization?: OrganizationCustomerDetailsResponseDto): BrandingFormValues => ({
  name: organization?.name ?? "",
  primaryColor: organization?.primaryColor ?? "#0F172A",
  secondaryColor: organization?.secondaryColor ?? "#E2E8F0",
  newLogo: undefined,
});

/**
 * Compact color field that matches the visual language of the other settings
 * pages while still using the native browser color picker underneath.
 */
const BrandColorField = ({
  label,
  value,
  onChange,
  disabled,
  accentClassName,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  accentClassName: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-gray-900">{label}</div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className={cn(
            "group relative flex h-14 w-full items-center gap-3 rounded-2xl border px-3 transition-all sm:max-w-[200px]",
            "border-gray-200 bg-gray-50/80 hover:border-gray-300 hover:bg-gray-100/80",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          <div
            className={cn(
              "h-8 w-8 shrink-0 rounded-xl border border-white/80 shadow-sm ring-1 ring-black/5",
              accentClassName,
            )}
            style={{ backgroundColor: value }}
          />
          <div className="min-w-0 text-left">
            <div className="text-xs font-medium uppercase tracking-[0.16em] text-gray-400">Farbe wählen</div>
          </div>

          <input
            ref={inputRef}
            type="color"
            value={value}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            disabled={disabled}
            className="pointer-events-none absolute inset-0 opacity-0"
          />
        </button>

        <Input
          value={value}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          disabled={disabled}
          className="h-11 rounded-xl border-gray-200 bg-white font-mono uppercase shadow-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          maxLength={7}
        />
      </div>
    </div>
  );
};

type ActiveLogoSelection = { type: "existing"; id: string } | { type: "new" };

const LogoGalleryItem = ({
  previewUrl,
  isActive,
  isAddTile = false,
  onClick,
}: {
  previewUrl?: string | null;
  isActive: boolean;
  isAddTile?: boolean;
  onClick: () => void;
}) => {
  return (
    <motion.button
      type="button"
      layout
      transition={{ 
        duration: 0.15,
        ease: "easeOut"
      }}
      onClick={onClick}
      className={cn(
        "group flex min-w-[72px] snap-start flex-col items-center gap-2 transition-all sm:min-w-[80px]",
        isActive ? "opacity-100" : "opacity-70 hover:opacity-100"
      )}
    >
      <div
        className={cn(
          "relative flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-2xl border transition-all duration-200 sm:h-20 sm:w-20",
          isActive 
            ? "border-blue-600 bg-blue-50/30 ring-4 ring-blue-500/10 shadow-sm" 
            : "border-gray-200 bg-white hover:border-gray-300",
          isAddTile && "border-dashed"
        )}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="" className="h-full w-full object-contain p-2.5" />
        ) : isAddTile ? (
          <Plus className={cn("h-5 w-5 transition-colors", isActive ? "text-blue-600" : "text-gray-400")} />
        ) : (
          <Building2 className="h-6 w-6 text-gray-300" />
        )}
      </div>
    </motion.button>
  );
};

const OrganizationBrandingSkeleton = () => {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-8">
      {/* Identity Card Skeleton */}
      <Card className="overflow-hidden border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100 bg-gray-50/30 px-5 py-6 sm:px-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-11 w-11 rounded-2xl" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-44 rounded-lg" />
              <Skeleton className="h-4 w-72 rounded-lg" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8 p-5 sm:p-6">
          {/* Logo Gallery */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-5 w-32 rounded-lg" />
              <Skeleton className="h-4 w-56 rounded-lg" />
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <Skeleton className="h-20 w-20 rounded-2xl" />
                </div>
              ))}
            </div>
            <Skeleton className="h-[200px] w-full rounded-3xl" />
          </div>

          {/* Form Fields */}
          <div className="space-y-8">
            <div className="space-y-3">
              <Skeleton className="h-5 w-32 rounded-lg" />
              <Skeleton className="h-11 w-full rounded-xl" />
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-3">
                <Skeleton className="h-5 w-24 rounded-lg" />
                <Skeleton className="h-16 w-full rounded-xl" />
              </div>
              <div className="space-y-3">
                <Skeleton className="h-5 w-24 rounded-lg" />
                <Skeleton className="h-16 w-full rounded-xl" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <Skeleton className="h-10 w-full rounded-xl sm:w-40" />
            <Skeleton className="h-10 w-full rounded-xl sm:w-44" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const OrganizationBranding = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManageBranding = can(Permission.MANAGE_ORGANIZATION);

  const { data: organization, isLoading } = useOrganizationsCustomerFindOneQuery();
  const updateOrganization = useOrganizationsCustomerUpdate();
  const { mutateAsync: uploadLogo } = useOrganizationsCustomerUploadLogo();
  const { mutateAsync: deleteLogo } = useOrganizationsCustomerDeleteLogo();
  const { mutateAsync: applyLogoEdit } = useOrganizationsCustomerApplyLogoEdit();
  const { mutateAsync: restoreOriginalLogo } = useOrganizationsCustomerRestoreOriginalLogo();
  const { mutateAsync: previewBackgroundRemoval } = useOrganizationsCustomerPreviewBackgroundRemoval();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mediaBusyAction, setMediaBusyAction] = useState<ManagedImageBusyAction>(null);
  const [busyLogoId, setBusyLogoId] = useState<string | null>(null);
  const [activeLogoSelection, setActiveLogoSelection] = useState<ActiveLogoSelection | null>(null);

  const form = useForm<BrandingFormValues>({
    resolver: zodResolver(brandingSchema),
    mode: "onChange",
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (!organization) {
      return;
    }

    form.reset(getDefaultValues(organization));
  }, [organization, form]);

  const watchedValues = form.watch();
  const selectedLogo = watchedValues.newLogo;
  const existingLogoStates = useMemo(() => organization?.logoStates || [], [organization?.logoStates]);
  const selectedExistingLogo = useMemo(
    () =>
      activeLogoSelection?.type === "existing"
        ? existingLogoStates.find((entry) => entry._id === activeLogoSelection.id) ?? null
        : null,
    [activeLogoSelection, existingLogoStates],
  );
  const selectedExistingLogoIndex = useMemo(
    () => (selectedExistingLogo ? existingLogoStates.findIndex((entry) => entry._id === selectedExistingLogo._id) : -1),
    [existingLogoStates, selectedExistingLogo],
  );
  const newLogoPreviewUrl = useMemo(() => {
    if (selectedLogo) {
      return URL.createObjectURL(selectedLogo);
    }

    return null;
  }, [selectedLogo]);

  useEffect(() => {
    if (!newLogoPreviewUrl?.startsWith("blob:")) {
      return;
    }

    return () => URL.revokeObjectURL(newLogoPreviewUrl);
  }, [newLogoPreviewUrl]);

  useEffect(() => {
    if (!activeLogoSelection) {
      setActiveLogoSelection(existingLogoStates[0] ? { type: "existing", id: existingLogoStates[0]._id } : { type: "new" });
      return;
    }

    if (activeLogoSelection.type === "existing") {
      const exists = existingLogoStates.some((entry) => entry._id === activeLogoSelection.id);
      if (!exists) {
        setActiveLogoSelection(existingLogoStates[0] ? { type: "existing", id: existingLogoStates[0]._id } : { type: "new" });
      }
    }
  }, [activeLogoSelection, existingLogoStates]);

  const isBusy = isSubmitting || mediaBusyAction !== null || updateOrganization.isPending;
  const canSubmit = form.formState.isValid && form.formState.isDirty && mediaBusyAction === null && canManageBranding;

  const runMediaAction = async ({
    action,
    task,
    successMessage,
    errorMessage,
    logoId,
  }: {
    action: Exclude<ManagedImageBusyAction, null>;
    task: () => Promise<unknown>;
    successMessage: string;
    errorMessage: string;
    logoId?: string;
  }) => {
    setMediaBusyAction(action);
    setBusyLogoId(logoId ?? null);

    try {
      await task();
      toast.success(successMessage);
    } catch (error) {
      toast.error(errorMessage, { error });
    } finally {
      setMediaBusyAction(null);
      setBusyLogoId(null);
    }
  };

  const onSubmit = async (values: BrandingFormValues) => {
    setIsSubmitting(true);

    try {
      await updateOrganization.mutateAsync({
        name: values.name.trim(),
        primaryColor: values.primaryColor.toUpperCase(),
        secondaryColor: values.secondaryColor.toUpperCase(),
      });

      await persistManagedImageDraft({
        entityId: "current-organization",
        file: values.newLogo,
        uploadImage: async ({ data }) => uploadLogo(data),
      });

      toast.success("Branding gespeichert");
      form.reset(getDefaultValues({
        ...organization,
        name: values.name.trim(),
        primaryColor: values.primaryColor.toUpperCase(),
        secondaryColor: values.secondaryColor.toUpperCase(),
      } as OrganizationCustomerDetailsResponseDto));
    } catch (error) {
      toast.error("Branding konnte nicht gespeichert werden", { error });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <SubPageLayout
        pageTitle="Branding"
        parentPageTitle="Dein Verein"
        parentPageCallback={() => navigate("/vereine")}
        navigateBackCallback={() => navigate("/vereine")}
        icon={<Palette className="h-6 w-6" />}
      >
        <OrganizationBrandingSkeleton />
      </SubPageLayout>
    );
  }

  if (!organization) {
    return (
      <SubPageLayout
        pageTitle="Branding"
        parentPageTitle="Dein Verein"
        parentPageCallback={() => navigate("/vereine")}
        navigateBackCallback={() => navigate("/vereine")}
        icon={<Palette className="h-6 w-6" />}
      >
        <div className="mx-auto flex min-h-[360px] w-full max-w-5xl items-center justify-center px-4 py-8 sm:px-6">
          <div className="flex max-w-md flex-col items-center gap-4 rounded-3xl border border-gray-200 bg-white px-8 py-10 text-center shadow-sm">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-100 bg-gray-50 text-gray-400">
              <Shield className="h-7 w-7" />
            </div>
            <div className="space-y-1">
              <div className="text-lg font-semibold text-gray-900">Keine Vereinsdaten gefunden</div>
              <div className="text-sm text-gray-500">Das Branding kann erst bearbeitet werden, wenn Vereinsdaten vorhanden sind.</div>
            </div>
          </div>
        </div>
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout
      pageTitle="Branding"
      parentPageTitle="Dein Verein"
      parentPageCallback={() => navigate("/vereine")}
      navigateBackCallback={() => navigate("/vereine")}
      icon={<Palette className="h-6 w-6" />}
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
            <Card className="overflow-hidden border-gray-200 shadow-sm">
              <CardHeader className="space-y-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/60 px-5 py-5 sm:px-6">
                <SectionHeader
                  icon={Shield}
                  title="Vereinsidentität"
                  description="Pflege Namen, Logos und Farben deines Vereins zentral für alle Templates und Automationen."
                />
              </CardHeader>

              <CardContent className="space-y-8 p-5 sm:p-6">
                {/* Logos Section */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="text-base font-semibold text-gray-900">Vereinslogos</div>
                    <div className="text-sm text-gray-500">Logos wechseln oder ergänzen.</div>
                  </div>

                  <div className="relative">
                    <div className="-mx-2 flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-2 py-3 no-scrollbar">
                      {existingLogoStates.map((logoEntry) => (
                        <LogoGalleryItem
                          key={logoEntry._id}
                          previewUrl={logoEntry.state.active?.url}
                          isActive={activeLogoSelection?.type === "existing" && activeLogoSelection.id === logoEntry._id}
                          onClick={() => setActiveLogoSelection({ type: "existing", id: logoEntry._id })}
                        />
                      ))}

                      <LogoGalleryItem
                        previewUrl={newLogoPreviewUrl}
                        isAddTile
                        isActive={activeLogoSelection?.type === "new"}
                        onClick={() => setActiveLogoSelection({ type: "new" })}
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white/40 to-transparent sm:w-20" />
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={
                        activeLogoSelection?.type === "existing"
                          ? `existing-${activeLogoSelection.id}`
                          : "new-logo"
                      }
                      initial={{ opacity: 0, y: 6, scale: 0.998 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -2, scale: 0.998 }}
                      transition={{ 
                        duration: 0.15, 
                        ease: [0.16, 1, 0.3, 1] 
                      }}
                      className="rounded-3xl border border-gray-200 bg-white p-4"
                    >
                    {activeLogoSelection?.type === "existing" && selectedExistingLogo ? (
                      <div className="space-y-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              Vereinslogo {selectedExistingLogoIndex + 1}
                            </div>
                          </div>
                          <div className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-600 ring-1 ring-gray-200">
                            {selectedExistingLogo.state.activeVariant === "edited" ? "Bearbeitet" : "Original"}
                          </div>
                        </div>

                        <ManagedImageField
                          value={selectedExistingLogo.state}
                          onFileChange={() => undefined}
                          onDeleteRemote={() =>
                            runMediaAction({
                              action: "delete",
                              logoId: selectedExistingLogo._id,
                              task: () => deleteLogo({ logoId: selectedExistingLogo._id }),
                              successMessage: "Vereinslogo wurde entfernt",
                              errorMessage: "Vereinslogo konnte nicht entfernt werden",
                            })
                          }
                          onApplyEdit={(edit) =>
                            runMediaAction({
                              action: "applyEdit",
                              logoId: selectedExistingLogo._id,
                              task: () => applyLogoEdit({ logoId: selectedExistingLogo._id, data: edit }),
                              successMessage: "Logo-Bearbeitung wurde übernommen",
                              errorMessage: "Logo konnte nicht bearbeitet werden",
                            })
                          }
                          onRestoreOriginal={() =>
                            runMediaAction({
                              action: "restoreOriginal",
                              logoId: selectedExistingLogo._id,
                              task: () => restoreOriginalLogo({ logoId: selectedExistingLogo._id }),
                              successMessage: "Original-Logo wurde wiederhergestellt",
                              errorMessage: "Original-Logo konnte nicht wiederhergestellt werden",
                            })
                          }
                          disabled={!canManageBranding || isSubmitting || (busyLogoId !== null && busyLogoId !== selectedExistingLogo._id)}
                          busyAction={busyLogoId === selectedExistingLogo._id ? mediaBusyAction : null}
                          emptyTitle="Vereinslogo auswählen"
                          previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
                        />
                      </div>
                    ) : (
                      <FormField
                        control={form.control}
                        name="newLogo"
                        render={() => (
                          <FormItem className="space-y-4">
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-gray-900">Weiteres Logo hinzufügen</div>
                              <div className="text-xs text-gray-500">Wird erst beim Speichern übernommen.</div>
                            </div>
                            <FormControl>
                              <ManagedImageField
                                file={selectedLogo}
                                onFileChange={(nextFile) =>
                                  form.setValue("newLogo", nextFile ?? undefined, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                                disabled={!canManageBranding || isSubmitting}
                                busyAction={mediaBusyAction}
                                emptyTitle="Weiteres Vereinslogo auswählen"
                                previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="flex flex-col gap-8">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel className="text-base font-semibold text-gray-900">Vereinsname</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            disabled={!canManageBranding || isBusy}
                            placeholder="z.B. FC Musterstadt"
                            className="h-11 rounded-xl border-gray-200 bg-white shadow-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-6">
                    <FormField
                      control={form.control}
                      name="primaryColor"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormControl>
                            <BrandColorField
                              label="Primärfarbe"
                              value={field.value}
                              onChange={field.onChange}
                              disabled={!canManageBranding || isBusy}
                              accentClassName="shadow-blue-500/10"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="secondaryColor"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormControl>
                            <BrandColorField
                              label="Sekundärfarbe"
                              value={field.value}
                              onChange={field.onChange}
                              disabled={!canManageBranding || isBusy}
                              accentClassName="shadow-slate-500/10"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-10 w-full rounded-xl px-4 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 sm:w-auto"
                    onClick={() => form.reset(getDefaultValues(organization))}
                    disabled={isBusy || !form.formState.isDirty}
                  >
                    <Undo2 className="mr-2 h-4 w-4" />
                    Änderungen verwerfen
                  </Button>

                  <div className="flex w-full sm:w-auto">
                    <PermissionFeatureWrapper
                      requiredPermission={Permission.MANAGE_ORGANIZATION}
                      requiredPlan={PlanKey.BASIC}
                    >
                      <Button
                        type="submit"
                        disabled={!canSubmit}
                        className={cn(
                          "h-10 w-full rounded-xl px-6 text-sm font-bold text-white shadow-md shadow-blue-500/20 transition-all",
                          "bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:scale-[0.98] disabled:opacity-50 sm:w-auto",
                        )}
                      >
                        {isBusy ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Speichern...
                          </>
                        ) : (
                          "Branding speichern"
                        )}
                      </Button>
                    </PermissionFeatureWrapper>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        </Form>
      </div>
    </SubPageLayout>
  );
};
